/**
 * Vorlage „Glasieren“.
 *
 * Der Kuchen steht auf dem Gitter. Aus dem Gießgefäß läuft Glasur auf die
 * Mitte, breitet sich über die Oberseite aus und läuft in Nasen an der Seite
 * herunter.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, shape } from '../scene.ts';
import type { SceneNode, Style } from '../scene.ts';
import { cake } from '../parts/food.ts';
import { coolingRack, pourer, stream } from '../parts/kitchen.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Gitter und Kuchen. */
const RACK_Y = GROUND_Y - 12;
const CAKE = { cx: 108, width: 124, height: 36, dome: 3 } as const;
const RX = CAKE.width / 2;
const RY = Math.max(4, CAKE.width * 0.16);
const TOP = RACK_Y - CAKE.height;
/** Ausgussecke des Gießgefäßes. */
const LIP = { x: 164, y: 42 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.08],
  tilt: [0.08, 0.16],
  pour: [0.14, 0.52],
  untilt: [0.5, 0.58],
  cupOut: [0.56, 0.62],
  cover: [0.16, 0.5],
  drips: [0.36, 0.78],
  reset: [0.88, 1],
} as const;

/** Glasuren. */
const GLAZES = { chocolate: 'Schokolade', icing: 'Zuckerguss' } as const;
type Glaze = keyof typeof GLAZES;

/** Nasen an der Vorderseite: Lage (Anteil von rx) und Länge. */
const DRIPS: readonly (readonly [number, number])[] = [
  [-0.86, 9],
  [-0.58, 16],
  [-0.24, 11],
  [0.12, 18],
  [0.46, 12],
  [0.78, 15],
];

/** Oberseite des Kuchens einschließlich Wölbung, wie in cake(). */
function topPath(): string {
  return path()
    .move(CAKE.cx - RX, TOP)
    .curve(
      CAKE.cx - RX * 0.9,
      TOP - RY - CAKE.dome * 1.6,
      CAKE.cx + RX * 0.9,
      TOP - RY - CAKE.dome * 1.6,
      CAKE.cx + RX,
      TOP,
    )
    .arc(RX, RY, false, true, CAKE.cx - RX, TOP)
    .close()
    .toString();
}

/** Eine Nase, die von der Vorderkante herabläuft. */
function drip(x: number, length: number, style: Style): SceneNode {
  const f = (x - CAKE.cx) / RX;
  const edgeY = TOP + RY * Math.sqrt(Math.max(0, 1 - f * f));
  const r = 3.5;
  return shape(
    path()
      .move(x - r, edgeY - 4)
      .line(x - r, edgeY + length - r)
      .arc(r, r, false, false, x + r, edgeY + length - r)
      .line(x + r, edgeY - 4)
      .close()
      .toString(),
    style,
  );
}

/** Vorlage „Glasieren“. */
export const bakeGlazeTemplate = defineTemplate({
  id: 'bake.glaze',
  group: 'bake',
  title: 'Glasieren',
  description:
    'Die Glasur wird mittig auf den ausgekühlten Kuchen gegossen. Sie verteilt sich über die Oberseite und läuft am Rand in Nasen herunter.',
  durationSeconds: 6,
  keywords: [
    'glasieren',
    'glasur',
    'überziehen',
    'zuckerguss',
    'guss',
    'kuvertüre darüber',
    'schokoladenglasur',
    'zitronenglasur',
    'puderzuckerglasur',
  ],
  stillTime: 0.6,
  parameters: [choice('glaze', 'Glasur', GLAZES, 'chocolate')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/schoko|kuvert|kakao/.test(lower)) return { glaze: 'chocolate' };
    if (/zucker|zitrone|puder|weiß/.test(lower)) return { glaze: 'icing' };
    return {};
  },
  scene(values, t) {
    const glaze = textValue(values, 'glaze', 'chocolate') as Glaze;
    const tone: Tone = glaze === 'icing' ? 'snow' : 'deep';
    const style: Style = {
      fill: tone,
      stroke: glaze === 'icing' ? 'cavity' : 'ink',
      strokeWidth: STROKE.detail,
    };
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const visible = 1 - reset;
    const flow =
      segment(t, TIME.pour[0], TIME.pour[0] + 0.03) * (1 - segment(t, TIME.pour[1] - 0.03, TIME.pour[1]));
    const tilt =
      -70 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
    const cover = ease.outCubic(segment(t, ...TIME.cover));
    const drips = ease.outQuad(segment(t, ...TIME.drips));

    const centre = { x: CAKE.cx + 6, y: TOP - RY * 0.3 - CAKE.dome };
    const glazeNodes: SceneNode[] = [];
    if (drips > 0.01) {
      DRIPS.forEach(([f, length]) => glazeNodes.push(drip(CAKE.cx + f * RX * 0.96, length * drips, style)));
    }
    if (cover > 0.01) {
      glazeNodes.push(
        group([shape(topPath(), style)], {
          transform: {
            scaleX: lerp(0.15, 1.02, cover),
            scaleY: lerp(0.3, 1.02, cover),
            ox: centre.x,
            oy: centre.y,
          },
        }),
      );
    }

    return compact([
      ...counter(),
      shadow(CAKE.cx, CAKE.width * 0.6),
      ...coolingRack(CAKE.cx, RACK_Y, CAKE.width + 36, GROUND_Y),
      ...cake(CAKE.cx, RACK_Y, CAKE.width, CAKE.height, CAKE.dome),
      glazeNodes.length > 0 ? faded(visible, glazeNodes) : null,
      flow > 0.02
        ? stream([LIP, { x: LIP.x - 12, y: LIP.y + 20 }, { x: centre.x + 4, y: centre.y }], 7 * flow, t, tone)
        : null,
      faded(cup, [pourer(LIP, 30, 36, tilt, lerp(0.85, 0.1, segment(t, ...TIME.pour)), tone)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: CAKE.cx - 6, y: TOP - 18 },
        { x: CAKE.cx - 46, y: TOP - 10 },
      ]),
      arrow([
        { x: CAKE.cx - RX * 0.58, y: TOP + RY },
        { x: CAKE.cx - RX * 0.58, y: TOP + RY + 20 },
      ]),
    ];
  },
});
